/* R28 Side Cart
 * Native replacement for the AMP side cart. Works with Dawn's product forms (it is the page's <cart-drawer>),
 * and with any custom section that adds to the cart with fetch or XHR (bundles, quiz, lookbook).
 * Public API: window.R28Cart.open(), .close(), .refresh({ open }), .add(items, { open })
 */
(() => {
  if (window.R28Cart) return;
  const root = document.querySelector('[data-r28c]');
  if (!root) return;

  const cfg = JSON.parse(root.querySelector('[data-r28c-config]').textContent);
  const S = cfg.settings;
  const T = S.text;
  const PAIR = cfg.pairings || {};
  const $ = (sel, el = root) => el.querySelector(sel);
  const el = {
    panel: $('.r28c__panel'), count: $('[data-r28c-count]'), ship: $('[data-r28c-ship]'),
    lines: $('[data-r28c-lines]'), empty: $('[data-r28c-empty]'), ups: $('[data-r28c-ups]'),
    track: $('[data-r28c-ups-track]'),
    total: $('[data-r28c-total]'), discounts: $('[data-r28c-discounts]'), checkoutAmt: $('[data-r28c-checkout-amt]'),
    express: $('[data-r28c-express]'), toast: $('[data-r28c-toast]'), scroll: $('[data-r28c-scroll]'),
  };
  const OWN = { 'X-R28C': '1' };
  const root_url = (cfg.routes.root || '/').replace(/\/$/, '');
  const url = (p) => root_url + p;

  let cart = cfg.cart;
  let lastFocus = null;
  let hitStops = new Set();
  let upsKey = '';
  const productCache = new Map();

  /* ---------- helpers ---------- */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const lang = document.documentElement.lang || 'en-GB';
  const money = (cents, cur = cart.currency) => {
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(lang.includes('-') ? lang : `${lang}-GB`, { style: 'currency', currency: cur, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(whole ? 0 : 2); }
  };
  const img = (src, w) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=' + w : '');
  const rate = () => parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1;
  const toLocal = (amount, eurOverride) => {
    if (!amount) return 0;
    if (cart.currency === 'EUR' && eurOverride) return Math.round(eurOverride * 100);
    // Exact conversion at the same rate Shopify uses to price the store in that currency, so the bar flips
    // at the same cent as an auto-converted shipping condition at checkout. No rounding.
    return Math.round(amount * 100 * rate());
  };
  const up = (v) => String(v == null ? '' : v).toUpperCase();
  const tpl = (str, map) => String(str || '').replace(/\{(\w+)\}/g, (_, k) => (k in map ? map[k] : ''));

  /* ---------- open / close ---------- */
  function open() {
    if (root.classList.contains('is-open')) return;
    lastFocus = lastFocus || document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('r28c-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      root.classList.add('is-open');
      el.panel.focus({ preventScroll: true });
    }));
    loadUpsells();
  }
  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('r28c-lock');
    setTimeout(() => { if (!root.classList.contains('is-open')) root.hidden = true; }, 460);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }
  root.addEventListener('click', (e) => { if (e.target.closest('[data-r28c-close]')) close(); });
  document.addEventListener('keydown', (e) => {
    if (!root.classList.contains('is-open')) return;
    if (e.key === 'Escape') return close();
    if (e.key !== 'Tab') return;
    const f = [...el.panel.querySelectorAll('a[href],button:not([disabled]),select,input,[tabindex]:not([tabindex="-1"])')].filter((n) => n.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // Header bag icon (and anything else in settings) opens the drawer instead of /cart.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (/\/cart\/?$/.test(location.pathname)) return;
    const t = e.target.closest(S.openSelectors + ',[data-r28c-open]');
    if (!t || root.contains(t)) return;
    e.preventDefault();
    lastFocus = t;
    open();
    refresh();
  });

  /* ---------- cart API ---------- */
  async function api(path, body) {
    const res = await window.__r28cFetch(url(path), {
      method: body ? 'POST' : 'GET',
      headers: Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, OWN),
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw Object.assign(new Error(data.description || data.message || T.error), { data, fromShopify: true });
    return data;
  }
  let refreshTimer, refreshWaiters = [];
  function refresh(opts = {}) {
    return new Promise((resolve) => {
      refreshWaiters.push({ resolve, open: !!opts.open });
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(async () => {
        const waiters = refreshWaiters; refreshWaiters = [];
        try { render(await api('/cart.js')); } catch (e) { /* keep last good state */ }
        if (waiters.some((w) => w.open)) open();
        waiters.forEach((w) => w.resolve(cart));
      }, 40);
    });
  }
  async function change(key, quantity, lineEl) {
    lineEl && lineEl.classList.add('is-busy');
    try {
      render(await api('/cart/change.js', { id: key, quantity }));
    } catch (e) {
      lineEl && lineEl.classList.remove('is-busy');
      const err = lineEl && lineEl.querySelector('[data-r28c-err]');
      if (err) { err.textContent = friendly(e); err.hidden = false; }
    }
  }
  async function add(items, opts = {}) {
    await api('/cart/add.js', { items });
    const fresh = await api('/cart.js');
    render(fresh, { newIds: items.map((i) => i.id) });
    document.dispatchEvent(new CustomEvent('r28:cart:added', { detail: { items, source: opts.source || 'api' } }));
    if (opts.open !== false) open();
    return fresh;
  }

  /* ---------- render ---------- */
  // /cart.js and the cart returned by /cart/change.js and /cart/update.js differ: the mutating endpoints nest
  // cart-level discounts as { amount, discount_application: {...} }. Flatten to the /cart.js shape so nothing
  // downstream has to care which endpoint the cart came from.
  function normaliseCart(c) {
    c = c || {};
    c.items = Array.isArray(c.items) ? c.items : [];
    c.discount_codes = (Array.isArray(c.discount_codes) ? c.discount_codes : []).filter((d) => d && d.code);
    c.cart_level_discount_applications = (c.cart_level_discount_applications || []).map((d) => {
      if (d && d.discount_application) return Object.assign({}, d.discount_application, { total_allocated_amount: d.amount != null ? d.amount : d.discount_application.total_allocated_amount });
      return d || {};
    });
    c.items.forEach((it) => {
      it.line_level_discount_allocations = (it.line_level_discount_allocations || []).filter((a) => a && a.discount_application);
    });
    c.item_count = c.item_count || 0;
    c.total_price = c.total_price || 0;
    return c;
  }
  // Draw each part on its own, so one bad value can never blank the rest of the cart.
  const safely = (name, fn) => { try { fn(); } catch (e) { console.error('[R28 cart] ' + name, e); } };
  // Customers see Shopify's own wording ("Only 2 left", "Code not valid"), never a JavaScript error.
  const friendly = (err) => (err && err.fromShopify && err.message ? err.message : T.error || 'Something went wrong. Please try again.');

  function render(next, opts = {}) {
    const prevTotal = cart ? cart.total_price : 0;
    cart = normaliseCart(next);
    const n = cart.item_count;
    root.classList.toggle('is-empty', n === 0);
    el.empty.hidden = n !== 0;
    el.count.textContent = n ? `(${n})` : '';
    el.total.textContent = money(cart.total_price);
    el.checkoutAmt.textContent = n ? money(cart.total_price) : '';
    if (el.express) el.express.hidden = n === 0;
    safely('bubble', () => renderBubble(n));
    safely('delivery bar', () => renderShip(prevTotal));
    safely('lines', () => renderLines(opts.newIds || []));
    safely('discounts', () => renderDiscounts());
    if (n === 0) { el.ups.hidden = true; upsKey = ''; }
    else if (root.classList.contains('is-open')) { if (!holdUps) loadUpsells(); }
    else upsKey = ''; // recompute next time it opens
  }

  function renderBubble(n) {
    document.querySelectorAll('#cart-icon-bubble').forEach((icon) => {
      let b = icon.querySelector('.cart-count-bubble');
      if (!n) { b && b.remove(); return; }
      if (!b) { b = document.createElement('div'); b.className = 'cart-count-bubble'; icon.appendChild(b); }
      b.innerHTML = `<span aria-hidden="true">${n < 100 ? n : ''}</span><span class="visually-hidden">${n} item${n === 1 ? '' : 's'}</span>`;
    });
  }

  function tiers() {
    const t = [];
    // Both free tiers are UK/EU only. Anywhere else the bar stays hidden rather than promising free delivery.
    const c = String(cfg.country || '').toUpperCase();
    if (S.deliveryCountries.length && !S.deliveryCountries.includes(c)) return t;
    const free = toLocal(S.freeThreshold, S.eurFree);
    if (free) t.push({ at: free, label: T.freeLabel, key: 'free' });
    const courier = toLocal(S.courierThreshold, S.eurCourier);
    if (courier && courier > free) t.push({ at: courier, label: T.courierLabel, key: 'courier' });
    return t;
  }

  function nextGap() {
    const t = tiers().find((x) => cart.total_price < x.at);
    return t ? { gap: t.at - cart.total_price, tier: t } : null;
  }

  const CHECK = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function renderShip(prevTotal) {
    const t = tiers();
    if (!t.length || !cart.item_count) { el.ship.innerHTML = ''; return; }
    const total = cart.total_price;
    const pending = t.find((x) => total < x.at);
    let msg, done = false;
    if (!pending) { msg = t.length > 1 ? T.gotCourier : T.gotFree; done = true; }
    else if (pending === t[0]) msg = tpl(T.toFree, { amount: `<strong>${money(pending.at - total)}</strong>` });
    else msg = tpl(T.toCourier, { amount: `<strong>${money(pending.at - total)}</strong>` });

    // One segment per stage, equal widths, each filling on its own. The active one carries a "£65 to go" tag.
    const segs = t.map((x, i) => {
      const from = i ? t[i - 1].at : 0;
      const hit = total >= x.at;
      const active = x === pending;
      const pct = hit ? 100 : Math.max(0, Math.min(100, ((total - from) / (x.at - from)) * 100));
      const pop = hit && prevTotal < x.at && !hitStops.has(x.key);
      if (hit) hitStops.add(x.key); else hitStops.delete(x.key);
      return { x, hit, active, pct, pop, gap: x.at - total };
    });
    const sig = t.map((x) => x.key).join('|');
    let wrap = el.ship.querySelector('.r28c__stages');
    if (!wrap || wrap.dataset.sig !== sig) {
      el.ship.innerHTML = `<p class="r28c__ship-msg"></p>
        <div class="r28c__stages" data-sig="${sig}" role="progressbar" aria-valuemin="0">
          ${t.map((x) => `<div class="r28c__stage" data-k="${x.key}">
            <div class="r28c__track"><div class="r28c__fill"></div><span class="r28c__togo" aria-hidden="true"></span><span class="r28c__dot">${CHECK}</span></div>
            <span class="r28c__stage-label"><b>${money(x.at)}</b>${esc(x.label)}</span>
          </div>`).join('')}
        </div>`;
      wrap = el.ship.querySelector('.r28c__stages');
    }
    const p = el.ship.querySelector('.r28c__ship-msg');
    p.className = 'r28c__ship-msg' + (done ? ' is-done' : '');
    p.innerHTML = (done ? `<span class="r28c__tick-ico">${CHECK}</span>` : '') + msg;
    const next = pending || t[t.length - 1];
    wrap.setAttribute('aria-valuemax', String(next.at / 100));
    wrap.setAttribute('aria-valuenow', String(Math.min(total, next.at) / 100));
    wrap.setAttribute('aria-valuetext', p.textContent.trim());
    segs.forEach((sg) => {
      const node = wrap.querySelector(`[data-k="${sg.x.key}"]`);
      node.className = 'r28c__stage' + (sg.hit ? ' is-hit' : '') + (sg.active ? ' is-active' : '') + (sg.pop ? ' is-pop' : '');
      node.style.setProperty('--p', sg.pct + '%');
      const tag = node.querySelector('.r28c__togo');
      tag.textContent = sg.active && sg.pct > 0 ? tpl(T.toGo || '{amount} to go', { amount: money(sg.gap) }) : '';
      requestAnimationFrame(() => { node.querySelector('.r28c__fill').style.width = sg.pct + '%'; });
    });
  }

  function renderLines(newIds) {
    el.lines.innerHTML = cart.items.map((it) => {
      const opts = (it.options_with_values || []).filter((o) => o.value !== 'Default Title').map((o) => `${esc(o.name)}: ${esc(o.value)}`).join(' · ');
      const props = Object.entries(it.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => `${esc(k)}: ${esc(v)}`).join(' · ');
      const sale = it.original_line_price > it.final_line_price;
      const discs = (it.line_level_discount_allocations || []).map((d) => `<p class="r28c__disc">${esc((d.discount_application || {}).title || '')} (−${money(d.amount || 0)})</p>`).join('');
      const isNew = newIds.includes(it.variant_id) || newIds.includes(it.id);
      return `<li class="r28c__line${isNew ? ' is-new' : ''}" data-key="${esc(it.key)}">
        <a class="r28c__img" href="${esc(it.url)}">${it.image ? `<img src="${esc(img(it.image, 200))}" alt="${esc(it.product_title)}" width="88" height="88" loading="lazy">` : ''}</a>
        <div class="r28c__info">
          <div class="r28c__row">
            <a class="r28c__name" href="${esc(it.url)}">${esc(it.product_title)}</a>
            <span class="r28c__price">${sale ? `<span class="r28c__was">${money(it.original_line_price)}</span>` : ''}${money(it.final_line_price)}</span>
          </div>
          ${opts ? `<p class="r28c__meta">${opts}</p>` : ''}
          ${props ? `<p class="r28c__meta">${props}</p>` : ''}
          ${discs}
          <div class="r28c__ctrls">
            <div class="r28c__qty">
              <button type="button" data-r28c-qty="-1" aria-label="Decrease quantity of ${esc(it.product_title)}">−</button>
              <span aria-live="polite">${it.quantity}</span>
              <button type="button" data-r28c-qty="1" aria-label="Increase quantity of ${esc(it.product_title)}">+</button>
            </div>
            <button type="button" class="r28c__rm" data-r28c-rm>${esc(T.remove || 'Remove')}</button>
          </div>
          <p class="r28c__err" data-r28c-err hidden></p>
        </div>
      </li>`;
    }).join('');
  }
  el.lines.addEventListener('click', (e) => {
    const line = e.target.closest('.r28c__line');
    if (!line) return;
    const key = line.dataset.key;
    const item = cart.items.find((i) => i.key === key);
    if (!item) return;
    if (e.target.closest('[data-r28c-rm]')) change(key, 0, line);
    const q = e.target.closest('[data-r28c-qty]');
    if (q) change(key, Math.max(0, item.quantity + Number(q.dataset.r28cQty)), line);
  });

  function renderDiscounts() {
    // Every saving in one place: automatic kit discounts and codes, with codes removable.
    const sums = new Map();
    const note = (d, amt) => {
      if (!d) return;
      const k = String(d.title || d.code || d.key || 'Discount');
      const cur = sums.get(k) || { title: k, code: d.type === 'discount_code', amount: 0 };
      cur.amount += amt; sums.set(k, cur);
    };
    (cart.cart_level_discount_applications || []).forEach((d) => note(d, d.total_allocated_amount || 0));
    cart.items.forEach((it) => (it.line_level_discount_allocations || []).forEach((a) => note(a.discount_application, a.amount || 0)));
    (cart.discount_codes || []).filter((c) => c.applicable).forEach((c) => {
      const hit = [...sums.values()].find((x) => up(x.title) === up(c.code));
      if (hit) { hit.code = true; hit.codeValue = c.code; } else sums.set(c.code, { title: c.code, code: true, codeValue: c.code, amount: 0 });
    });
    el.discounts.innerHTML = [...sums.values()].map((d) => `
      <p class="r28c__disc-row">
        <span class="r28c__disc-name">${d.code ? `<span class="r28c__tag">${esc(d.codeValue || d.title)}<button type="button" data-r28c-uncode="${esc(d.codeValue || d.title)}" aria-label="${esc(tpl(T.codeRemove, { code: d.codeValue || d.title }))}">×</button></span>` : esc(d.title)}</span>
        <span>${d.amount ? '−' + money(d.amount) : esc(T.codeAtCheckout)}</span>
      </p>`).join('');
  }

  /* ---------- discount codes ---------- */
  const code = {
    toggle: $('[data-r28c-code-toggle]'), form: $('[data-r28c-code-form]'),
    input: $('[data-r28c-code-input]'), msg: $('[data-r28c-code-msg]'),
  };
  // Automatic (non-code) savings in the bag right now: title -> amount.
  function automaticSavings() {
    const m = new Map();
    const add = (d, amt) => { if (d && d.type !== 'discount_code') { const t = String(d.title || 'Discount'); m.set(t, (m.get(t) || 0) + (amt || 0)); } };
    (cart.cart_level_discount_applications || []).forEach((d) => add(d, d.total_allocated_amount));
    cart.items.forEach((it) => (it.line_level_discount_allocations || []).forEach((a) => add(a.discount_application, a.amount)));
    return m;
  }
  const appliedCodes = () => (cart.discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
  function codeMsg(text, bad) {
    if (!code.msg) return;
    code.msg.textContent = text || '';
    code.msg.classList.toggle('is-bad', !!bad);
  }
  async function setCodes(list) {
    // /cart/update.js replaces the bag's codes with exactly this list.
    return render(await api('/cart/update.js', { discount: list.join(',') }));
  }
  if (code.toggle) {
    code.toggle.addEventListener('click', () => {
      const openNow = code.form.hidden;
      code.form.hidden = !openNow;
      code.toggle.setAttribute('aria-expanded', String(openNow));
      if (openNow) code.input.focus();
    });
    code.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = code.input.value.trim();
      if (!value) return code.input.focus();
      const btn = code.form.querySelector('button');
      btn.disabled = true; codeMsg('');
      const keep = appliedCodes().filter((c) => up(c) !== up(value));
      const autosBefore = automaticSavings();
      try {
        await setCodes(keep.concat(value));
        const hit = (cart.discount_codes || []).find((c) => up(c.code) === up(value));
        const autosAfter = automaticSavings();
        if (hit && hit.applicable) {
          // The code won. If it pushed out a kit saving that doesn't combine, say so plainly.
          const lost = [...autosBefore.keys()].filter((t) => !autosAfter.has(t));
          code.input.value = '';
          code.form.hidden = true;
          code.toggle.setAttribute('aria-expanded', 'false');
          if (lost.length) codeMsg(tpl(T.codeReplaced, { code: hit.code, saving: lost.join(', ') }));
          else { codeMsg(tpl(T.codeApplied, { code: hit.code })); setTimeout(() => codeMsg(''), 2500); }
        } else {
          await setCodes(keep); // don't leave a dead code sitting in the bag
          // Shopify keeps whichever saving is bigger when discounts don't combine.
          const kept = [...automaticSavings().keys()];
          codeMsg(kept.length ? tpl(T.codeNoCombine, { code: up(value), saving: kept.join(', ') }) : tpl(T.codeInvalid, { code: up(value) }), true);
          code.input.select();
        }
      } catch (err) {
        codeMsg(friendly(err), true);
      } finally {
        btn.disabled = false;
      }
    });
  }
  el.discounts.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-r28c-uncode]');
    if (!b) return;
    b.disabled = true;
    try { await setCodes(appliedCodes().filter((c) => c !== b.dataset.r28cUncode)); codeMsg(''); }
    catch (err) { codeMsg(friendly(err), true); b.disabled = false; }
  });

  /* ---------- recommendations ---------- */
  /* ---- live catalogue: new products join the recommendations without editing the pairing map ----
     Read once per visit from /products.json (products published to the online store), cached for 30 minutes.
     Optional product tags give you control without code:
       r28-family:outer      put the product in a category (overrides the automatic guess)
       r28-pair:<handle>     when this product is in the bag, recommend <handle> first
       r28-hide              never recommend this product                                              */
  const CAT = new Map(); // handle -> { ti, ty, tg[], pub }
  let catalogPromise = null;
  const asTags = (t) => (Array.isArray(t) ? t : String(t || '').split(',')).map((x) => String(x).trim().toLowerCase()).filter(Boolean);
  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    const KEY = 'r28c:catalog:v1:' + root_url;
    try {
      const hit = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (hit && Date.now() - hit.t < 30 * 60 * 1000) { hit.p.forEach((x) => CAT.set(x.h, x)); return (catalogPromise = Promise.resolve()); }
    } catch (e) { /* storage blocked */ }
    catalogPromise = (async () => {
      const all = [];
      for (let page = 1; page <= 4; page++) {
        const r = await window.__r28cFetch(url(`/products.json?limit=250&page=${page}`), { headers: OWN });
        if (!r.ok) break;
        const d = await r.json();
        (d.products || []).forEach((x) => all.push({ h: x.handle, ti: x.title || '', ty: x.product_type || '', tg: asTags(x.tags), pub: x.published_at || x.created_at || null }));
        if (!d.products || d.products.length < 250) break;
      }
      all.forEach((x) => CAT.set(x.h, x));
      try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), p: all })); } catch (e) { /* ignore */ }
    })().catch(() => {});
    return catalogPromise;
  }
  const tagValues = (h, prefix) => ((CAT.get(h) || {}).tg || []).filter((t) => t.startsWith(prefix)).map((t) => t.slice(prefix.length).trim()).filter(Boolean);

  function guessFamily(text) {
    const s = String(text || '').toLowerCase();
    if (!s.trim()) return null;
    if (/gift.?card/.test(s)) return null;
    if (/tight/.test(s)) return 'bib_tights';
    if (/\bbibs?\b|bib-short|bib short/.test(s)) return 'bib_shorts';
    if (/long.?sleeve/.test(s) && /jersey/.test(s)) return 'ls_jersey';
    if (/jersey/.test(s)) return 'ss_jersey';
    if (/jacket|gilet|vest|cape/.test(s)) return 'outer';
    if (/overshoe/.test(s)) return 'overshoes';
    if (/calf|guard/.test(s)) return 'calf_guards';
    if (/aero.?socks?\b/.test(s)) return 'aero_socks';
    if (/sock/.test(s)) return 'socks';
    if (/glove|mitt/.test(s)) return 'gloves';
    if (/sleeve|warmer/.test(s)) return 'warmers';
    if (/base.?layer|\bbra\b/.test(s)) return 'base_layer';   // before "suit": base layers are typed as skinsuits
    if (/suit/.test(s)) return 'race_suit';
    if (/\btee\b|t-?shirt|\bcap\b|beanie|\bhat\b|hoodie/.test(s)) return 'casual';
    return null;
  }
  const famOf = (() => {
    const map = {};
    Object.entries(PAIR.families || {}).forEach(([f, hs]) => hs.forEach((h) => { map[h] = f; }));
    const known = new Set(Object.keys(PAIR.families || {}));
    return (handle, type = '', title = '') => {
      const tagged = tagValues(handle, 'r28-family:').find((f) => known.has(f));
      if (tagged) return tagged;
      if (map[handle]) return map[handle];
      const c = CAT.get(handle) || {};
      // Name first (handle + title), then product type: suit types read "...Skinsuits And Aero Base layers".
      return guessFamily(`${handle} ${title || c.ti || ''}`) || guessFamily(type || c.ty);
    };
  })();
  const isWomens = (h, title) => /^womens?-|-womens?(-|$)/.test(h) || /\bwom[ae]n'?s?\b/i.test(title || (CAT.get(h) || {}).ti || '');
  const lineOf = (h) => (PAIR.lines || []).filter((l) => h.includes(l) || (l === 'ltd-edition' && h.includes('privateer')));
  const unisex = new Set(PAIR.unisex || []);
  const exclude = new Set(PAIR.exclude || []);
  const isHidden = (h) => {
    if (exclude.has(h)) return true;
    const c = CAT.get(h) || {};
    return (c.tg || []).includes('r28-hide') || /archive/i.test(h + ' ' + (c.ti || '')) || /gift.?card/i.test(h + ' ' + (c.ty || ''));
  };
  const isNew = (h) => {
    const pub = (CAT.get(h) || {}).pub;
    return !!(S.newDays && pub && Date.now() - Date.parse(pub) < S.newDays * 864e5);
  };
  // Every product in a category: the curated order from the pairing map, then anything new from the catalogue.
  function pool(f) {
    const listed = PAIR.families?.[f] || [];
    const seen = new Set(listed);
    const extra = [...CAT.keys()].filter((h) => !seen.has(h) && famOf(h) === f);
    return listed.concat(extra);
  }

  /* ---- kits from the Bundle Builder page (one source of truth with /pages/bundles and the PDP widget) ---- */
  let bundlesPromise = null;
  function loadBundles() {
    if (!S.bundlesUrl) return Promise.resolve([]);
    if (bundlesPromise) return bundlesPromise;
    const KEY = 'r28c:bundles:' + S.bundlesUrl;
    try {
      const hit = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (hit && Date.now() - hit.t < 30 * 60 * 1000) return (bundlesPromise = Promise.resolve(hit.b));
    } catch (e) { /* storage blocked */ }
    bundlesPromise = window.__r28cFetch(url(S.bundlesUrl.replace(root_url, '')), { headers: OWN })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const m = html.match(/data-bb-data>([\s\S]*?)<\/script>/);
        const b = m ? (JSON.parse(m[1]).bundles || []) : [];
        try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), b })); } catch (e) { /* ignore */ }
        return b;
      })
      .catch(() => []);
    return bundlesPromise;
  }

  // For each kit the bag has started, offer the missing pieces first, with the saving the kit earns.
  //  mode "set":    every piece 10% (or the kit's %) off once the whole kit is in the bag
  //  mode "addons": the suit unlocks % off its socks / overshoes / calf guards
  function bundleCandidates(bundles) {
    const out = [];
    const items = cart.items;
    const has = (h) => items.find((i) => i.handle === h);
    bundles.forEach((b) => {
      ['men', 'women'].forEach((g) => {
        const pieces = b['products_' + g] || [];
        const other = b['products_' + (g === 'men' ? 'women' : 'men')] || [];
        const choice = b['choice_' + g] || [];
        if (!pieces.length) return;
        // Slot 0 can be filled by its alternatives (e.g. the summer long sleeve in the OTNR kit).
        const slotItem = (h, i) => has(h) || (i === 0 ? choice.map(has).find(Boolean) : null);
        const filled = pieces.map(slotItem);
        // Only gender-specific pieces start a kit; Pro Socks alone shouldn't pull in a men's and a women's jersey.
        const starters = filled.filter((it, i) => it && !other.includes(pieces[i]));
        if (!starters.length) return;
        const anchor = starters[0];
        const missing = pieces.map((h, i) => (filled[i] ? null : { h, i })).filter(Boolean);
        const pct = Number(b.pct) || 0;
        if (b.mode === 'addons') {
          const suitIn = !!filled[0];
          missing.forEach(({ h, i }) => {
            if (i === 0) out.push({ h, anchor, score: 120, meta: { note: tpl(T.bundleSuit, { pct, name: b.name }) } });
            else if (suitIn) out.push({ h, anchor, score: 330, meta: { note: tpl(T.bundleAddon, { pct }), pct } });
          });
        } else {
          missing.forEach(({ h }) => out.push({
            h, anchor, score: 300 + starters.length * 10,
            meta: { note: tpl(missing.length === 1 ? T.bundleComplete : T.bundlePart, { pct, name: b.name }) },
          }));
          if (!missing.length) (b['addons_' + g] || []).forEach((h, k) => out.push({ h, anchor, score: 200 - k * 5, meta: { note: T.bundleLayer } }));
        }
      });
    });
    return out;
  }

  function rankCandidates(bundles = []) {
    const inCart = new Set(cart.items.map((i) => i.handle));
    const famsInCart = new Set(cart.items.map((i) => famOf(i.handle, i.product_type, i.product_title)));
    const scores = new Map(); // handle -> { score, anchor, meta }
    bundleCandidates(bundles).forEach((c) => {
      if (inCart.has(c.h) || isHidden(c.h)) return;
      const cur = scores.get(c.h);
      if (!cur || c.score > cur.score) scores.set(c.h, { score: c.score, anchor: c.anchor, meta: c.meta, kit: true });
    });
    // cart.items is newest first, so recent adds weigh most.
    cart.items.forEach((item, idx) => {
      const weight = Math.max(0.55, 1 - idx * 0.12);
      const aFam = famOf(item.handle, item.product_type, item.product_title);
      const aWomen = isWomens(item.handle, item.product_title);
      const aLines = lineOf(item.handle);
      const consider = (h, base, explicit) => {
        if (inCart.has(h) || isHidden(h)) return;
        const f = famOf(h);
        if (!explicit && !f) return;
        if (!unisex.has(f)) { if (aWomen !== isWomens(h)) return; }
        let s = base;
        if (lineOf(h).some((l) => aLines.includes(l))) s += 20;
        if (isNew(h)) s += 25; // new products get seen ahead of their older siblings
        if (!explicit && famsInCart.has(f)) {
          if (!unisex.has(f)) return; // one pair of bibs is enough; don't offer a second
          s -= 70; // a second accessory is possible, just less likely
        }
        s *= weight;
        const cur = scores.get(h);
        if (!cur || s > cur.score) scores.set(h, { score: s, anchor: item });
      };
      const explicitPairs = tagValues(item.handle, 'r28-pair:').concat(PAIR.pairs?.[item.handle] || []);
      explicitPairs.forEach((h, i) => consider(h, 160 - i * 10, true));
      (PAIR.targets?.[aFam] || []).forEach((tf, rank) => {
        pool(tf).forEach((h, pos) => consider(h, 100 - rank * 15 - Math.min(pos, 10) * 2, false));
      });
    });
    return [...scores.entries()].sort((a, b) => b[1].score - a[1].score).map(([handle, v]) => ({ handle, ...v }));
  }

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const p = window.__r28cFetch(url(`/products/${handle}.js`), { headers: OWN })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    productCache.set(handle, p);
    return p;
  }

  async function shopifyRecs(productId, intent) {
    if (!S.useShopifyRecs || !cfg.routes.recs) return [];
    try {
      const r = await window.__r28cFetch(`${cfg.routes.recs}.json?product_id=${productId}&limit=6&intent=${intent}`, { headers: OWN });
      const d = await r.json();
      return (d.products || []).map((p) => { productCache.set(p.handle, Promise.resolve(p)); return p.handle; });
    } catch (e) { return []; }
  }

  async function loadUpsells() {
    if (!cart || !cart.item_count) return;
    const key = cart.items.map((i) => `${i.variant_id}x${i.quantity}`).join(',') + '|' + cart.total_price;
    if (key === upsKey) return;
    upsKey = key;
    const want = S.upsellCount;
    if (!el.track.children.length) {
      el.ups.hidden = false;
      el.track.innerHTML = Array.from({ length: 2 }, () => '<div class="r28c__up is-skel"></div>').join('');
    }
    // One product per family first (bib shorts, socks, gilet...) so the row isn't three versions of the same thing.
    // Missing kit pieces always lead; they carry a real saving.
    const [bundles] = await Promise.all([loadBundles(), loadCatalog()]);
    const scored = rankCandidates(bundles);
    const seenFam = new Set();
    const first = [], rest = [];
    scored.forEach((c) => {
      const f = famOf(c.handle) || c.handle;
      (c.kit || !seenFam.has(f) ? first : rest).push(c);
      seenFam.add(f);
    });
    const ranked = first.concat(rest);
    const inCartIds = new Set(cart.items.map((i) => i.product_id));
    let picks = [];
    const tryHandles = async (list) => {
      const prods = await Promise.all(list.map((c) => getProduct(c.handle)));
      prods.forEach((p, i) => {
        if (!p || !p.available || inCartIds.has(p.id) || picks.some((x) => x.p.id === p.id)) return;
        picks.push({ p, anchor: list[i].anchor, score: list[i].score, meta: list[i].meta || null });
      });
    };
    await tryHandles(ranked.slice(0, want + 3));
    if (picks.length < want && cart.items[0]) {
      for (const intent of ['complementary', 'related']) {
        if (picks.length >= want) break;
        const hs = (await shopifyRecs(cart.items[0].product_id, intent)).filter((h) => !isHidden(h));
        await tryHandles(hs.map((h, i) => ({ handle: h, anchor: cart.items[0], score: 10 - i })));
      }
    }
    if (key !== upsKey) return; // cart changed while loading
    picks = picks.slice(0, want);

    // If one of the top picks closes the gap to the next delivery tier on its own, lead with it.
    const g = nextGap();
    if (g && !(picks[0] && picks[0].meta)) {
      const i = picks.findIndex((x, idx) => idx < 3 && x.p.price >= g.gap);
      if (i > 0) picks.unshift(picks.splice(i, 1)[0]);
    }
    safely('recommendations', () => renderUpsells(picks, g));
  }

  /* ---- size matching ---- */
  const LADDER = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const normLetter = (v) => {
    const s = String(v).toUpperCase().replace(/\s+/g, '').replace(/^2XL$/, 'XXL').replace(/^3XL$/, 'XXXL').replace(/^2XS$/, 'XXS');
    return LADDER.includes(s) ? s : null;
  };
  const range = (v) => {
    const m = String(v).match(/(\d{2})\s*[-–]\s*(\d{2})/);
    return m ? [Number(m[1]), Number(m[2])] : null;
  };
  const sizeIndex = (product) => (product.options || []).findIndex((o) => /size/i.test(typeof o === 'string' ? o : o.name));
  const optName = (o) => (typeof o === 'string' ? o : o.name);
  const optValues = (product, i) => {
    const o = product.options[i];
    if (o && o.values) return o.values;
    return [...new Set(product.variants.map((v) => v.options[i]))];
  };

  function cartSizes(anchor) {
    // The anchor item first, then everything else in the bag.
    const items = [anchor, ...cart.items.filter((i) => i !== anchor)];
    const out = { letter: null, letterFrom: null, range: null, rangeFrom: null };
    items.forEach((it) => {
      (it.options_with_values || []).forEach((o) => {
        if (!/size/i.test(o.name)) return;
        const l = normLetter(o.value);
        if (l && !out.letter) { out.letter = l; out.letterFrom = o.value; }
        const r = range(o.value);
        if (r && !out.range) { out.range = r; out.rangeFrom = o.value; }
      });
    });
    return out;
  }

  function matchSize(values, sizes) {
    // Same letter size (L jersey -> L bibs).
    if (sizes.letter) {
      const v = values.find((x) => normLetter(x) === sizes.letter);
      if (v) return { value: v, from: sizes.letterFrom };
    }
    // Shoe-size ranges: pick the one that overlaps most (42-44 overshoes -> 42-48 Pro Socks).
    if (sizes.range) {
      let best = null, bestOv = 0;
      values.forEach((x) => {
        const r = range(x);
        if (!r) return;
        const ov = Math.min(r[1], sizes.range[1]) - Math.max(r[0], sizes.range[0]);
        if (ov >= 0 && (best === null || ov > bestOv)) { best = x; bestOv = ov; }
      });
      if (best) return { value: best, from: sizes.rangeFrom };
    }
    return null;
  }

  function anchorValue(anchor, name) {
    const o = (anchor.options_with_values || []).find((x) => x && String(x.name || '').toLowerCase() === String(name || '').toLowerCase());
    return o ? o.value : null;
  }

  function initialSelection(p, anchor) {
    const sel = [];
    const sIdx = sizeIndex(p);
    let matched = null;
    p.options.forEach((o, i) => {
      const vals = optValues(p, i);
      if (i === sIdx) {
        const inStock = vals.filter((x) => p.variants.some((v) => v.options[i] === x && v.available));
        const m = vals.length === 1 ? { value: vals[0] }
          : matchSize(vals, cartSizes(anchor)) || (inStock.length === 1 ? { value: inStock[0] } : null);
        sel[i] = m ? m.value : null;
        if (m && m.from) matched = m.from;
      } else {
        // Same colour as the anchor when it exists, else the first combination in stock.
        // Copy the anchor's colour only between garments (grey jersey -> grey bibs), never from socks or gloves.
        const garment = (h, t) => { const f = famOf(h, t); return f && !unisex.has(f); };
        let same = garment(anchor.handle, anchor.product_type) && garment(p.handle, p.type) ? anchorValue(anchor, optName(o)) : null;
        if (!same && /gender/i.test(optName(o))) same = vals.find((x) => (isWomens(anchor.handle) ? /women/i : /^men/i).test(x));
        const avail = p.variants.filter((v) => v.available).map((v) => v.options[i]);
        sel[i] = same && vals.includes(same) && avail.includes(same) ? same : (avail[0] || vals[0]);
      }
    });
    // If the matched size is sold out in that combination, don't preselect it.
    if (sIdx > -1 && sel[sIdx] && !findVariant(p, sel)?.available) { sel[sIdx] = null; matched = null; }
    return { sel, matched };
  }

  function findVariant(p, sel) {
    if (sel.some((x) => x == null)) return null;
    return p.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
  }

  const upState = new Map(); // product id -> { p, sel, matched, anchor }
  let badgePid = null; // only the lead card that closes the gap gets the "Unlocks..." note
  let holdUps = false; // keep the list still while an upsell add finishes its animation

  // Rule 28 colourway names -> swatch fill. Add new colourways here.
  const SWATCH = {
    black: '#16171a', white: '#ffffff', grey: '#8d9093', gray: '#8d9093', 'light grey': '#cfd1d3', charcoal: '#3b3d40',
    navy: '#1f2b47', pink: '#e7a3b7', lilac: '#c4a8d9', purple: '#5e3c8c', fluro: '#d8f43c', blue: '#2f5bd3', red: '#c8352c',
  };
  function swatchOf(val) {
    const parts = String(val).toLowerCase().split('/').map((x) => x.trim());
    const cols = parts.map((x) => SWATCH[x]);
    if (cols.some((c) => !c)) return null;
    return cols.length === 1 ? cols[0] : `linear-gradient(135deg, ${cols[0]} 50%, ${cols[1]} 50%)`;
  }

  function renderUpsells(picks, gap) {
    if (!picks.length) { el.ups.hidden = true; el.track.innerHTML = ''; upState.clear(); return; }
    el.ups.hidden = false;
    const before = new Set(upState.keys());
    upState.clear();
    badgePid = gap && picks[0].p.price >= gap.gap ? String(picks[0].p.id) : null;
    el.track.innerHTML = picks.map(({ p, anchor, meta }) => {
      const { sel, matched } = initialSelection(p, anchor);
      upState.set(String(p.id), { p, sel, matched, anchor, meta });
      const fresh = before.size && !before.has(String(p.id));
      return `<article class="r28c__up${fresh ? ' is-in' : ''}" data-pid="${p.id}">${upInner(p.id, gap)}</article>`;
    }).join('');
  }

  const CHEV = '<svg viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function upInner(pid, gap = nextGap(), state = {}) {
    const { p, sel, matched, meta } = upState.get(String(pid));
    const sIdx = sizeIndex(p);
    const v = findVariant(p, sel);
    const price = v ? v.price : p.price;
    const compare = v ? v.compare_at_price : p.compare_at_price;
    const imgSrc = (v && v.featured_image && v.featured_image.src) || p.featured_image;
    const href = esc(p.url || '/products/' + p.handle);
    const unlock = gap && String(pid) === badgePid && price >= gap.gap ? (gap.tier.key === 'courier' ? T.unlocksCourier : T.unlocksFree) : '';
    const priceHtml = meta && meta.pct
      ? `<s>${money(price)}</s> ${money(Math.round(price * (1 - meta.pct / 100)))}`
      : `${compare > price ? `<s>${money(compare)}</s> ` : ''}${money(price)}`;

    // Colour shows as swatches (the image follows the pick). Other options (speed range, pack size)
    // sit beside the price as a quiet text menu. Unknown colour names fall back to the menu.
    const menus = p.options.map((o, i) => {
      const vals = optValues(p, i);
      if (i === sIdx || vals.length < 2) return '';
      const name = optName(o);
      if (/colou?r/i.test(name) && vals.every(swatchOf)) {
        return `<div class="r28c__sw" role="radiogroup" aria-label="${esc(name)}">${vals.map((val) => {
          const out = !p.variants.some((x) => x.options[i] === val && x.available);
          const on = sel[i] === val;
          return `<button type="button" role="radio" class="r28c__sw-btn" data-opt="${i}" data-val="${esc(val)}" aria-checked="${on}" aria-label="${esc(val)}${out ? ' (sold out)' : ''}" title="${esc(val)}" ${out ? 'aria-disabled="true"' : ''} tabindex="${on ? 0 : -1}" style="--sw:${swatchOf(val)}"></button>`;
        }).join('')}<span class="r28c__sw-name">${esc(sel[i] || '')}</span></div>`;
      }
      return `<label class="r28c__menu"><span class="visually-hidden">${esc(name)}</span><select data-opt="${i}">${vals.map((val) =>
        `<option value="${esc(val)}"${sel[i] === val ? ' selected' : ''}>${esc(val)}</option>`).join('')}</select>${CHEV}</label>`;
    }).join('');

    let seg = '';
    if (sIdx > -1 && optValues(p, sIdx).length > 1) {
      const vals = optValues(p, sIdx);
      seg = `<div class="r28c__seg${state.attn ? ' is-attn' : ''}" role="radiogroup" aria-label="${esc(optName(p.options[sIdx]))}" style="--n:${vals.length}">${vals.map((val) => {
        const out = !p.variants.some((x) => x.options[sIdx] === val && x.available && x.options.every((o2, j) => j === sIdx || sel[j] == null || o2 === sel[j]));
        const on = sel[sIdx] === val;
        return `<button type="button" role="radio" class="r28c__seg-btn" data-opt="${sIdx}" data-val="${esc(val)}" aria-checked="${on}" ${out ? 'aria-disabled="true"' : ''} tabindex="${on || (sel[sIdx] == null && val === vals[0]) ? 0 : -1}">${esc(val)}</button>`;
      }).join('')}</div>`;
    }

    const why = state.attn ? `<span class="r28c__why-attn">${esc(T.chooseSize)}</span>`
      : [meta && meta.note ? `<span class="r28c__why-kit">${esc(meta.note)}</span>` : '',
         unlock ? `<span class="r28c__why-ship">${esc(unlock)}</span>` : '',
         !meta && !unlock && matched && sel[sIdx] ? `<span>${tpl(T.matched, { size: esc(matched) })}</span>` : ''].filter(Boolean).join('');
    const soldOut = v && !v.available;
    const label = state.done ? `<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="visually-hidden">${esc(T.added)}</span>`
      : esc(soldOut ? T.soldOut : T.add);
    return `
      <a class="r28c__up-img" href="${href}" tabindex="-1">${imgSrc ? `<img src="${esc(img(String(imgSrc).startsWith('//') ? 'https:' + imgSrc : imgSrc, 160))}" alt="" width="64" height="64" loading="lazy">` : ''}</a>
      <div class="r28c__up-main">
        <div class="r28c__up-top">
          <div class="r28c__up-head">
            <a class="r28c__up-name" href="${href}">${esc(p.title)}</a>
            <div class="r28c__up-meta"><span class="r28c__up-price">${priceHtml}</span>${menus}</div>
          </div>
          <button type="button" class="button button--primary r28c__add${state.done ? ' is-done' : ''}" data-r28c-add ${soldOut || state.busy || state.done ? 'disabled' : ''} aria-label="${esc(T.add)} ${esc(p.title)}${v ? ', ' + esc(v.title) : ''}">${label}</button>
        </div>
        ${why ? `<p class="r28c__why">${why}</p>` : ''}
        ${seg}
      </div>`;
  }

  const reduceMotion = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  function collapse(card) {
    if (reduceMotion()) return Promise.resolve();
    const h = card.offsetHeight;
    card.style.height = h + 'px';
    card.getBoundingClientRect();
    card.classList.add('is-out');
    card.style.height = '0px';
    return new Promise((r) => setTimeout(r, 320));
  }

  el.track.addEventListener('click', async (e) => {
    const card = e.target.closest('.r28c__up');
    if (!card || card.classList.contains('is-skel')) return;
    const pid = card.dataset.pid;
    const st = upState.get(pid);
    if (!st) return;
    const segBtn = e.target.closest('.r28c__seg-btn, .r28c__sw-btn');
    if (segBtn) {
      if (segBtn.getAttribute('aria-disabled') === 'true') return;
      const isSize = segBtn.classList.contains('r28c__seg-btn');
      st.sel[Number(segBtn.dataset.opt)] = segBtn.dataset.val;
      if (isSize) st.matched = null;
      // A colour change can make the chosen size unavailable; clear it rather than add a sold-out variant.
      const sIdx = sizeIndex(st.p);
      if (!isSize && sIdx > -1 && st.sel[sIdx] != null && !findVariant(st.p, st.sel)?.available) { st.sel[sIdx] = null; st.matched = null; }
      card.innerHTML = upInner(pid);
      card.querySelector(`.${isSize ? 'r28c__seg-btn' : 'r28c__sw-btn'}[data-opt="${segBtn.dataset.opt}"][data-val="${CSS.escape(segBtn.dataset.val)}"]`)?.focus();
      return;
    }
    const btn = e.target.closest('[data-r28c-add]');
    if (!btn || btn.disabled) return;
    const sIdx = sizeIndex(st.p);
    if (sIdx > -1 && st.sel[sIdx] == null) {
      // No disabled button to puzzle over: point at the size control instead.
      card.innerHTML = upInner(pid, undefined, { attn: true });
      card.querySelector('.r28c__seg-btn:not([aria-disabled="true"])')?.focus();
      return;
    }
    const v = findVariant(st.p, st.sel);
    if (!v) return;
    card.innerHTML = upInner(pid, undefined, { busy: true });
    holdUps = true;
    try {
      await add([{ id: v.id, quantity: 1, properties: { _source: 'cart_upsell' } }], { source: 'upsell', open: true });
      if (window.dataLayer) window.dataLayer.push({ event: 'r28_cart_upsell_add', product: st.p.handle, variant: v.id, anchor: st.anchor && st.anchor.handle });
      card.innerHTML = upInner(pid, undefined, { done: true });
      await new Promise((r) => setTimeout(r, reduceMotion() ? 300 : 650));
      await collapse(card);
    } catch (err) {
      card.innerHTML = upInner(pid);
      toast(friendly(err));
    } finally {
      holdUps = false;
      upsKey = '';
      loadUpsells();
    }
  });
  el.track.addEventListener('keydown', (e) => {
    // Arrow keys move along the size control, like a native segmented control.
    const b = e.target.closest('.r28c__seg-btn, .r28c__sw-btn');
    if (!b || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();
    const all = [...b.parentElement.querySelectorAll(`.${b.classList[0]}:not([aria-disabled="true"])`)];
    const next = all[(all.indexOf(b) + (e.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
    next && next.click();
  });
  el.track.addEventListener('change', (e) => {
    const s = e.target.closest('.r28c__menu select');
    if (!s) return;
    const card = s.closest('.r28c__up');
    const st = upState.get(card.dataset.pid);
    st.sel[Number(s.dataset.opt)] = s.value;
    card.innerHTML = upInner(card.dataset.pid);
    card.querySelector(`.r28c__menu select[data-opt="${s.dataset.opt}"]`)?.focus();
  });

  let toastTimer;
  function toast(msg) {
    el.toast.textContent = msg;
    el.toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.toast.classList.remove('is-on'), 2200);
  }

  /* ---------- hook into the theme ---------- */
  // 1) Dawn product forms look for a <cart-drawer> and call these methods.
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class extends HTMLElement {
      getSectionsToRender() { return []; }
      renderContents() { refresh({ open: S.openOnAdd }); }
      setActiveElement(el) { lastFocus = el; }
      open() { open(); }
      close() { close(); }
    });
  } else {
    console.warn('[R28 cart] Another cart-drawer is defined. Set Theme settings > Cart > Cart type to "Page".');
  }

  // 2) Anything else that changes the cart (bundles, quiz, lookbook, apps) refreshes the drawer.
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  window.__r28cFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const u = typeof input === 'string' ? input : (input && input.url) || '';
    const p = window.__r28cFetch(input, init);
    const own = init && init.headers && (init.headers['X-R28C'] || (init.headers.get && init.headers.get('X-R28C')));
    if (!own && CART_RE.test(u)) {
      const isAdd = /\/cart\/add/.test(u);
      p.then((r) => { if (r.ok) refresh({ open: isAdd && S.openOnAdd }); }).catch(() => {});
    }
    return p;
  };
  const xOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (m, u) {
    if (CART_RE.test(String(u))) {
      const isAdd = /\/cart\/add/.test(String(u));
      this.addEventListener('load', () => { if (this.status < 400) refresh({ open: isAdd && S.openOnAdd }); });
    }
    return xOpen.apply(this, arguments);
  };

  // Express checkout: Shopify draws its wallet buttons in a sealed grid with one slot per wallet, so hiding
  // a button with CSS leaves an empty slot. Instead, trim the wallet list and let Shopify draw a fresh set.
  //   Phones ("smart"): Apple Pay where the device can use it, otherwise Shop Pay.
  //   Everywhere: optionally drop Google Pay (still offered at checkout).
  function tuneWallets() {
    safely('express checkout', () => {
      const host = root.querySelector('[data-r28c-express] shopify-accelerated-checkout-cart');
      if (!host || host.hasAttribute('data-r28c-tuned')) return;
      let list;
      try { list = JSON.parse(host.getAttribute('wallet-configs') || '[]'); } catch (e) { return; }
      if (!Array.isArray(list) || !list.length) return;
      const name = (w) => String((w && w.name) || '').toLowerCase();
      const phone = window.matchMedia && matchMedia('(max-width: 749px)').matches;
      let keep = list;
      if (S.hideGooglePay) keep = keep.filter((w) => !/google/.test(name(w)));
      if (phone && (S.expressMobile || 'smart') === 'smart') {
        let apple = false;
        try { apple = !!(window.ApplePaySession && ApplePaySession.canMakePayments()); } catch (e) { apple = false; }
        const pick = (apple && keep.find((w) => /apple/.test(name(w)))) || keep.find((w) => /shop_pay/.test(name(w))) || keep[0];
        keep = pick ? [pick] : keep;
      }
      if (!keep.length || keep.length === list.length) { host.setAttribute('data-r28c-tuned', ''); return; }
      const fresh = host.cloneNode(false);
      fresh.setAttribute('wallet-configs', JSON.stringify(keep));
      fresh.setAttribute('data-r28c-tuned', '');
      host.replaceWith(fresh);
    });
  }
  tuneWallets();
  // Shopify's wallet script can load after ours; check again once the page has settled.
  window.addEventListener('load', () => setTimeout(tuneWallets, 50));

  // 3) The bundle builder and PDP bundle widget announce their adds too.
  ['cart:refresh', 'r28:cart:refresh'].forEach((ev) => document.addEventListener(ev, () => refresh()));

  // 4) Back/forward cache can restore a stale cart.
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });

  window.R28Cart = { open, close, refresh, add, get cart() { return cart; } };
  render(cart);
  root.hidden = true;
})();
